/**
 * Canadian French locale for devices-ui.
 * Export is the "devices" subtree: host apps merge this into their i18n
 * resources as translation.devices so that t('devices.list.heading') etc. resolve.
 */
import type { DevicesLocale } from './en'

export const devicesFrCa = {
  // ---------------------------------------------------------------------------
  // Common / shared labels
  // ---------------------------------------------------------------------------
  common: {
    cancel: `Annuler`,
    delete: `Supprimer`,
    save: `Enregistrer`,
    saveAndReboot: `Enregistrer et redémarrer`,
    actions: `Actions`,
    error: `Erreur`,
    yes: `Oui`,
    no: `Non`,
    all: `Tous`,
    unchanged: `Inchangé`,
    loading: `Chargement...`,
    none: `— Aucun —`,
    backToList: `Retour à la liste`,
  },

  // ---------------------------------------------------------------------------
  // Options used across multiple components
  // ---------------------------------------------------------------------------
  options: {
    transportUdp: `UDP`,
    transportTcp: `TCP`,
    transportTls: `TLS`,
    jitterNormal: `Normal`,
    jitterLow: `Faible`,
    jitterHigh: `Élevé`,
    natNormal: `Normal`,
    natStun: `STUN`,
    serverGroupPrimary: `Primaire`,
    serverGroupSecondary: `Secondaire`,
    sourceMango: `Mango Neuf`,
    sourceRefurbished: `Mango Reconditionné`,
    sourceCustomer: `Client`,
    wifiSecurityNone: `NONE`,
    wifiSecurityWep: `WEP`,
    wifiSecurityWpa: `WPA/WPA2 PSK`,
    wifiSecurityWpa3: `WPA3-Personal`,
    timezoneAlaska: `Alaska`,
    timezoneArizona: `Arizona`,
    timezoneAtlantic: `Atlantic Time (Canada)`,
    timezoneCentral: `Central Time (US & Canada)`,
    timezoneEastern: `Eastern Time (US & Canada)`,
    timezoneGreenwich: `Greenwich Mean Time (UK)`,
    timezoneHawaii: `Hawaii`,
    timezoneIndiana: `Indiana (East)`,
    timezoneMountain: `Mountain Time (US & Canada)`,
    timezonePacific: `Pacific Time (US & Canada)`,
    timezonePuertoRico: `Puerto Rico`,
  },

  // ---------------------------------------------------------------------------
  // Device form (add / edit)
  // ---------------------------------------------------------------------------
  form: {
    addDevice: `Ajouter un appareil`,
    editDevice: `Modifier l'appareil`,
    createDevice: `Créer l'appareil`,
    loadingDevice: `Chargement de l'appareil...`,

    // Section headers
    deviceInfo: `Informations de l'appareil`,
    advanced: `Avancé`,
    displaySettings: `Paramètres d'affichage`,

    // Field labels
    model: `Modèle`,
    selectModel: `Sélectionner le modèle d'appareil…`,
    mostCommonYealink: `Plus commun (Yealink)`,
    macAddress: `Adresse MAC`,
    macPlaceholder: `AA:BB:CC:DD:EE:FF`,
    generateRandomMac: `Générer une adresse MAC aléatoire`,
    generateRandomMacConfirmTitle: `Remplacer l'adresse MAC?`,
    generateRandomMacConfirmDescription: `La génération d'une adresse MAC aléatoire remplacera la valeur actuelle. Voulez-vous vraiment continuer?`,
    generateRandomMacConfirmAction: `Générer une nouvelle MAC`,
    copyProvisioningMac: `Copier la MAC pour l'URL d'approvisionnement`,
    copyProvisioningMacHint: `Copier en minuscules, sans deux-points (pour l'URL d'approvisionnement)`,
    copyProvisioningMacCopied: `Copié {{value}}`,
    copyProvisioningMacFailed: `Échec de la copie. Sélectionnez la valeur manuellement.`,
    checkingUniqueness: `Vérification de l'unicité…`,
    macRequired: `L'adresse MAC est requise`,
    macInvalid: `L'adresse MAC n'est pas valide`,
    macAlreadyInUse: `L'adresse MAC est déjà utilisée`,
    modelRequired: `Le modèle de l'appareil est requis.`,
    warrantyExpiresAt: `Garantie expire le`,
    source: `Source`,
    lastProvisionedTime: `Dernier approvisionnement`,
    lastProvisionedFrom: `Dernier approvisionnement depuis`,
    assignedUser: `Utilisateur assigné`,
    assignedUserRequired: `L'utilisateur assigné est requis.`,
    directory: `Répertoire`,
    directoryRequired: `Le répertoire est requis.`,
    selectDirectory: `Sélectionner le répertoire...`,
    primaryLineExtension: `Extension de la ligne principale`,
    noExtensionsForUser: `L'utilisateur assigné n'a aucune extension de ligne valide. Sélectionnez un autre utilisateur ou assignez une extension de ligne avant d'enregistrer.`,
    primaryLineExtensionRequired: `L'extension de ligne principale est requise.`,
    timeZone: `Fuseau horaire`,
    stationName: `Nom de la station`,
    stationNameRequired: `Le nom de la station est requis.`,
    stationNamePlaceholder: `p. ex. Réception`,
    enableDaylightSavings: `Activer l'heure d'été`,
    cannotSaveTitle: `Impossible d'enregistrer pour le moment`,
    buttonConfigurationInvalid: `Corrigez la configuration de boutons non valide avant d'enregistrer.`,

    // Advanced section labels
    pages: `Pages`,
    lineKeysAvailable: `Touches de ligne disponibles`,
    selectModelFirst: `Sélectionner d'abord le modèle...`,
    lineKeysHelper: `({{pages}} page{{pageSuffix}} x {{keysPerPage}} touches = {{total}} au total)`,
    allowProvisioning: `Autoriser l'approvisionnement`,
    sidecarEnabled: `Sidecar activé`,
    transport: `Transport`,
    jitter: `Gigue`,
    serverGroupHost: `Hôte du groupe de serveurs`,
    natTraversal: `Traversée NAT`,
    wifiEnabled: `WiFi activé`,
    wifiNetworkSsid: `SSID du réseau WiFi`,
    wifiSecurityMode: `Mode de sécurité WiFi`,
    wifiPassword: `Mot de passe WiFi`,
    acousticShieldEnabled: `Bouclier acoustique activé`,
  },

  // ---------------------------------------------------------------------------
  // Device preview card
  // ---------------------------------------------------------------------------
  preview: {
    title: `Aperçu de l'appareil`,
    unavailable: `Aperçu non disponible pour le modèle sélectionné.`,
    emptyAlt: `Aperçu d'appareil vide`,
  },

  // ---------------------------------------------------------------------------
  // Button grid
  // ---------------------------------------------------------------------------
  buttons: {
    linesAndButtons: `Lignes et boutons`,
    sidecarButtons: `Boutons du sidecar`,
    lineButtonHelper_one: `{{count}} emplacement de bouton pour {{modelName}}. La rangée 1 est toujours Ligne et suit l'extension de la ligne principale.`,

    lineButtonHelper_other: `{{count}} emplacements de bouton pour {{modelName}}. La rangée 1 est toujours Ligne et suit l'extension de la ligne principale.`,
    sidecarButtonHelper_one: `{{count}} emplacement de sidecar.`,

    sidecarButtonHelper_other: `{{count}} emplacements de sidecar.`,
    noSlotsForModel: `Aucun emplacement de bouton disponible pour ce modèle.`,
    selectModelForSlots: `Sélectionner un modèle d'appareil pour voir les emplacements de boutons.`,
    columnNumber: `#`,
    columnFunction: `Fonction`,
    columnValue: `Valeur`,
    columnLabel: `Étiquette`,
    phoneNumberPlaceholder: `Numéro de téléphone`,
    optionalLabelPlaceholder: `Étiquette optionnelle`,
    extensionNone: `-- Aucune --`,

    // Button function options
    functionBlank: `Vide`,
    functionLine: `Ligne`,
    functionRollover: `Renvoi`,
    functionSpeedDial: `Composition abrégée`,
    functionPresence: `BLF / Présence`,
    functionParkingLot: `Parc d'appel`,
    functionSharedVoicemail: `Messagerie vocale partagée`,

    // Preset speed dial options
    presetMangoSupport: `Support Mango`,
    presetLoginQueue: `Connexion file d'attente`,
    presetLogoutQueue: `Déconnexion file d'attente`,
    presetBlockCaller: `Bloquer l'appelant`,
    presetOpenClose: `Ouvert/Fermé`,
    presetPauseResumeRecording: `Pause/Reprise d'enregistrement`,
    presetAlternateVm: `Messagerie vocale alternative`,
    preset911History: `Historique 911`,

    // Preset button labels
    presetLabelMangoSupport: `Support Mango`,
    presetLabelLoginQ: `Conn. Q`,
    presetLabelLogoutQ: `Déconn. Q`,
    presetLabelBlock: `Bloquer`,
    presetLabelOpenClose: `Ouvert/Fermé`,
    presetLabelPauseResume: `Pause/Reprise`,
    presetLabelAltVm: `MV alt.`,
    presetLabel911Hist: `Hist. 911`,

    parkingLotOption: `Parc d'appel {{number}}`,

    multipleLineWarningTitle: `Plusieurs boutons Ligne détectés`,
    multipleLineWarning: `L'ajout de plusieurs boutons Ligne peut entraîner des résultats indésirables. Assurez-vous que l'extension sélectionnée n'est pas assignée à un autre bouton Ligne et n'est configurée sur aucun autre appareil.`,
    multipleLineWarningProceed: `Continuer`,
    multipleLineWarningCancel: `Annuler`,
  },

  // ---------------------------------------------------------------------------
  // Clone dialog
  // ---------------------------------------------------------------------------
  clone: {
    title: `Cloner l'appareil`,
    description: `Créer un nouvel appareil à partir de "{{sourceLabel}}". Entrer une adresse MAC unique. Tous les paramètres et boutons seront copiés vers le nouvel appareil.`,
    loadingSettings: `Chargement des paramètres de l'appareil…`,
    stationLabelPlaceholder: `Clone de l'appareil`,
    cloneButton: `Cloner l'appareil`,
  },

  // ---------------------------------------------------------------------------
  // Delete dialog
  // ---------------------------------------------------------------------------
  deleteDialog: {
    title: `Supprimer l'appareil`,
    confirmation: `Êtes-vous sûr de vouloir supprimer "{{name}}" ? Cette action ne peut pas être annulée.`,
    unnamedDevice: `Appareil sans nom`,
  },

  // ---------------------------------------------------------------------------
  // Mass edit dialog
  // ---------------------------------------------------------------------------
  massEdit: {
    title: `Modification en masse des appareils`,
    description_one: `Mise à jour de {{count}} appareil. Laissez un champ inchangé pour conserver la valeur actuelle.`,

    description_other: `Mise à jour de {{count}} appareils. Laissez un champ inchangé pour conserver la valeur actuelle.`,
    transport: `Transport`,
    natTraversal: `Traversée NAT`,
    jitter: `Gigue`,
    timeZone: `Fuseau horaire`,
    daylightSaving: `Heure d'été`,
    allowProvisioning: `Autoriser l'approvisionnement`,
    serverGroupHost: `Hôte du groupe de serveurs`,
    saveButton_one: `Enregistrer {{count}} appareil`,

    saveButton_other: `Enregistrer {{count}} appareils`,
  },

  // ---------------------------------------------------------------------------
  // Set desired firmware dialog
  // ---------------------------------------------------------------------------
  setFirmware: {
    title: `Définir la version du micrologiciel souhaitée`,
    description: `Définir la version du micrologiciel souhaitée pour {{count}} appareil(s) sélectionné(s). La configuration sera rechargée après la mise à jour.`,
    label: `Version du micrologiciel souhaitée`,
    placeholder: `p. ex. 1.2.3.4`,
    confirmButton: `Définir et recharger`,
  },

  // ---------------------------------------------------------------------------
  // List page
  // ---------------------------------------------------------------------------
  list: {
    heading: `Appareils`,
    searchPlaceholder: `Rechercher par nom, utilisateur, extension ou MAC...`,
    addDevice: `Ajouter un appareil`,
    actionsButton: `Actions`,
    actionsWithCount: `Actions ({{count}} sélectionné{{suffix}})`,
    massEditAction: `Modification en masse ({{count}})`,
    reloadConfigAction: `Recharger la configuration ({{count}})`,
    reloadConfigRebootAction: `Recharger la config et redémarrer ({{count}})`,
    setFirmwareAction: `Définir le micrologiciel souhaité ({{count}})`,
    rebuildAll: `Reconstruire tout`,
    rebuildAndReboot: `Reconstruire et redémarrer`,
    rebuildAllConfirmTitle: `Reconstruire toutes les configurations d'appareils?`,
    rebuildAllConfirmDescription: `Vous êtes sur le point de reconstruire la configuration de tous les appareils de ce PBX. Voulez-vous vraiment continuer?`,
    rebuildAllConfirmAction: `Tout reconstruire`,
    rebuildAndRebootConfirmTitle: `Reconstruire et redémarrer tous les appareils?`,
    rebuildAndRebootConfirmDescription: `Vous êtes sur le point de reconstruire la configuration de tous les appareils de ce PBX et de les redémarrer. Voulez-vous vraiment continuer?`,
    rebuildAndRebootConfirmAction: `Reconstruire et redémarrer`,
    pbxRequired: `PBX est requis pour la page Appareils.`,
    failedToLoad: `Échec du chargement des appareils`,
    noMatchingDevices: `Aucun appareil ne correspond à vos critères de recherche`,

    // Column headers
    columnName: `Nom`,
    columnPrimaryExt: `Ext. principale`,
    columnUser: `Utilisateur`,
    columnModel: `Modèle`,
    columnMacAddress: `Adresse MAC`,
    columnFirmware: `Micrologiciel`,
    columnWarranty: `Garantie`,
    columnLastCheckIn: `Dernière vérification`,
    columnInitializedOn: `Initialisé le`,
    columnConfiguration: `Configuration`,
    columnActions: `Actions`,
    selectAllDevices: `Sélectionner tous les appareils`,
  },

  // ---------------------------------------------------------------------------
  // Device row
  // ---------------------------------------------------------------------------
  row: {
    loadingDevices: `Chargement des appareils...`,
    noDevices: `Aucun appareil`,
    noDevicesDescription: `Il n'y a pas encore d'appareils pour ce PBX.`,
    selectDevice: `Sélectionner {{name}}`,
    editDevice: `Modifier {{name}}`,
    modelFallback: `Modèle {{id}}`,
    reportsModel: `Rapports : {{model}}`,
    warrantyInWarranty: `Sous garantie`,
    warrantyExpired: `Expirée`,
    warrantyUnknown: `Aucune garantie`,
    changesPending: `Modifications en attente`,
    sidecar: `Sidecar`,
    noProv: `Aucun appro`,
    fromSource: `de {{source}}`,
    desiredFirmware: `(souhaité : {{version}})`,
    firmwareUnknown: `inconnu`,
    rowActions: `Actions de rangée`,
    cloneAction: `Cloner`,
    deleteAction: `Supprimer`,
  },

  // ---------------------------------------------------------------------------
  // Filters
  // ---------------------------------------------------------------------------
  filters: {
    make: `Fabricant`,
    model: `Modèle`,
    firmware: `Micrologiciel`,
    firmwarePlaceholder: `Tous`,
    warranty: `Garantie`,
    transport: `Transport`,
    jitter: `Gigue`,
    sidecar: `Sidecar`,
    provisioning: `Approvisionnement`,
    clearFilters: `Effacer les filtres`,
    warrantyUnknown: `Aucune garantie`,
    warrantyInWarranty: `Sous garantie`,
    warrantyExpired: `Expirée`,
    provisioningEnabled: `Activé`,
    provisioningDisabled: `Désactivé`,
    provisioningChangesPending: `Modifications en attente`,
    toggleFilters: `Afficher/masquer les filtres`,
  },

  // ---------------------------------------------------------------------------
  // Pagination / toolbar
  // ---------------------------------------------------------------------------
  pagination: {
    resultsPerPage: `Résultats par page :`,
    totalCount: `({{count}} au total)`,
    previousPage: `Page précédente`,
    nextPage: `Page suivante`,
    all: `Tous`,
    jumpToPage: `Aller à la page {{page}}`,
  },

  // ---------------------------------------------------------------------------
  // Toast messages
  // ---------------------------------------------------------------------------
  toast: {
    deviceCreated: `Appareil créé`,
    createFailed: `Échec de la création de l'appareil`,
    deviceUpdated: `Appareil mis à jour`,
    deviceUpdatedRebooting: `Appareil mis à jour et en cours de redémarrage`,
    updateFailed: `Échec de la mise à jour de l'appareil`,
  },
} as const satisfies DevicesLocale
